import type { FunctionComponent, PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";

import { ClientOnly } from "~/utils/client-only";

import { datadogRum } from "@datadog/browser-rum";
import type { DurationInput } from "duration-fns";
import { toMilliseconds } from "duration-fns";
// Eslint is not able to resolve this for some reason, but it imports correctly
// eslint-disable-next-line import/no-unresolved
import { useRegisterSW } from "virtual:pwa-register/react";

type ServiceWorkerUpdaterContext = {
  isUpdating: boolean;
  update: () => void;
  isUpdateAvailable: boolean;
};

const Context = createContext<ServiceWorkerUpdaterContext | null>(null);

export const useServiceWorkerUpdater = () => {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error(
      "useServiceWorkerUpdater must be used within a ServiceWorkerUpdaterProvider",
    );
  }
  return ctx;
};

export const ServiceWorkerUpdaterProvider: FunctionComponent<
  PropsWithChildren<{
    checkForUpdateInterval?: DurationInput;
    isAuthenticated?: boolean;
  }>
> = ({
  children,
  checkForUpdateInterval = { seconds: 60 },
  isAuthenticated,
}) => {
  // Create a server-safe version of the context with no-op functions
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const update = () => {
    setIsUpdating(true);
  };

  return (
    <Context.Provider value={{ isUpdating, update, isUpdateAvailable }}>
      {children}

      {/* Use ClientOnly to mount the client-side implementation */}
      {isAuthenticated ? (
        <ClientOnly fallback={null}>
          {() => (
            <ServiceWorkerManager
              checkForUpdateInterval={checkForUpdateInterval}
              isAuthenticated={isAuthenticated}
              setIsUpdateAvailable={setIsUpdateAvailable}
              isUpdating={isUpdating}
            />
          )}
        </ClientOnly>
      ) : null}
    </Context.Provider>
  );
};

const ServiceWorkerManager: FunctionComponent<{
  checkForUpdateInterval: DurationInput;
  isAuthenticated: boolean;
  isUpdating: boolean;
  setIsUpdateAvailable: (value: boolean) => void;
}> = ({
  isAuthenticated,
  setIsUpdateAvailable,
  isUpdating,
  checkForUpdateInterval,
}) => {
  // Keep a ref to interval used to check for updates. Should clear this when the page unmounts,
  // ensuring there is no memory leakage.
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, []);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, r) {
      console.log(`Service worker at: ${swUrl}`);
      if (r) {
        // Clear any existing intervals
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
        }

        intervalIdRef.current = setInterval(() => {
          r.update();
        }, toMilliseconds(checkForUpdateInterval));
      }
    },
    onRegistered(registration) {
      console.log("In registration", registration);
      if (registration) {
        datadogRum.addAction("sw-registered", {
          timestamp: Date.now(),
          isAuthenticated,
        });
      }
    },
    // Leaving this commented out function, in case we need to use it for quick debugging
    // onNeedRefresh() {
    //   console.log("Needs to refresh!");
    // },
    onRegisterError(error) {
      datadogRum.addError(error, { extraMessage: "sw-registration-failed" });
    },
    onOfflineReady() {
      datadogRum.addAction("sw-offline-ready", {});
    },
  });

  useEffect(() => {
    setIsUpdateAvailable(needRefresh);
  }, [needRefresh, setIsUpdateAvailable]);

  useEffect(() => {
    if (isUpdating) {
      updateServiceWorker(true);
    }
  }, [isUpdating, updateServiceWorker]);

  return null;
};
